import type SplitIO from '@splitsoftware/splitio/types/splitio';
import type {
  Attributes,
  IManager,
} from '@splitsoftware/splitio/types/splitio';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { User } from 'src/shared/graphql/generated';
import {
  useGetTreatmentQuery,
  useGetTreatmentWithConfigQuery,
} from 'src/shared/graphql/generated';
import { useSplitOverride } from 'src/shared/hooks';
import type { WrappedSplitClient } from 'src/shared/hooks/useSplit/types';

type SplitContext = {
  attributes: SplitIO.Attributes;
  client: SplitIO.IClient | null;
  clientPromise: Promise<SplitIO.IClient | undefined> | null;
  manager: IManager | null;
  managerPromise: Promise<IManager | undefined> | null;
  updateAttributes: (user?: User) => void;
  updateUserId: (id: string) => void;
  clearUser: () => void;
};

const defaultValue: SplitContext = {
  attributes: {},
  clientPromise: null,
  client: null,
  managerPromise: null,
  manager: null,
  updateAttributes: () => {},
  updateUserId: () => {},
  clearUser: () => {},
};

const context = createContext(defaultValue);
const { Provider } = context;
const useSplit = () => {
  const {
    attributes,
    clientPromise,
    managerPromise,
    updateUserId,
    updateAttributes,
    clearUser,
  } = useContext(context);

  const wrappedClient = async (): Promise<WrappedSplitClient> => {
    const client = await clientPromise;
    return {
      getTreatment: (experiment: string, attr: SplitIO.Attributes = {}) =>
        client?.getTreatment(experiment, { ...attributes, ...attr }),
      getTreatmentWithConfig: (
        experiment: string,
        attr: SplitIO.Attributes = {},
      ) =>
        client?.getTreatmentWithConfig(experiment, { ...attributes, ...attr }),
    };
  };
  return {
    client: wrappedClient(),
    manager: managerPromise,
    updateUserId,
    updateAttributes,
    clearUser,
  };
};

/**
 * Read the initial treatment and config for a flag from the Apollo cache
 * populated when treatments are read during server rendering.
 *
 * @notes this preserves the the same initial value behavior of
 * `getTreatmentWithConfig` below in that it defaults to the `control` with an
 * empty object for `config` to prevent modifying the existing client-side
 * behavior of `useTreatmentWithConfig` when adding server-side support.
 *
 * @see {@link src/server/routers/ssr/graphql/split.ts}
 */
const useServerTreatmentWithConfig = <TConfig extends {}>(flagName: string) => {
  const treatmentWithConfig = useGetTreatmentWithConfigQuery({
    variables: { flagName },
  }).data?.getTreatmentWithConfig;

  const control = { treatment: 'control', config: {} };

  if (!treatmentWithConfig) return control;

  const { treatment, config } = treatmentWithConfig;

  try {
    return {
      treatment: treatment ?? 'control',
      config: config ? (JSON.parse(config) as TConfig) : {},
    };
  } catch (error) {
    return control;
  }
};

function useTreatmentWithConfig<TConfig>(flagName: string): {
  treatment: string;
  config: Partial<TConfig>;
} {
  const override = useSplitOverride(flagName);

  const {
    attributes,
    clientPromise,
    client: initialClient,
  } = useContext(context);

  const { treatment: serverTreatment, config: serverConfig } =
    useServerTreatmentWithConfig(flagName);

  const getTreatmentWithConfig = useCallback(
    (client: SplitIO.IClient | undefined | null) => {
      if (override) {
        return override;
      }
      const { treatment: treatmentValue, config: treatmentConfig } =
        client?.getTreatmentWithConfig(flagName, attributes) ?? {};

      return {
        treatment: treatmentValue ?? 'control',
        config: treatmentConfig ? (JSON.parse(treatmentConfig) as TConfig) : {},
      };
    },
    [attributes, flagName, override],
  );

  const [treatment, setTreatment] = useState<string>(() =>
    initialClient
      ? getTreatmentWithConfig(initialClient).treatment
      : serverTreatment,
  );
  const [config, setConfig] = useState<Partial<TConfig>>(() =>
    initialClient ? getTreatmentWithConfig(initialClient).config : serverConfig,
  );

  useEffect(() => {
    void (async () => {
      const client = await clientPromise;
      const { treatment: treatmentValue, config: treatmentConfig } =
        getTreatmentWithConfig(client);

      setTreatment(treatmentValue);
      setConfig(treatmentConfig);
    })();
  }, [clientPromise, getTreatmentWithConfig]);

  return { treatment, config };
}

/**
 * Read the initial treatment for a flag from the Apollo cache populated when
 * treatments are read during server rendering.
 *
 * @notes This preserves the the same initial value behavior of `getTreatment`
 * below in that it defaults to the `undefined` to prevent modifying the
 * existing client-side behavior of `useTreatment` when adding server-side
 * support.
 *
 * @see {@link src/server/routers/ssr/graphql/split.ts}
 */
const useServerTreatment = (flagName: string) =>
  useGetTreatmentQuery({ variables: { flagName } }).data?.getTreatment ??
  undefined;

const useTreatment = (
  flagName: string,
  overrideAttributes: Attributes = {},
) => {
  const override = useSplitOverride(flagName);

  const {
    attributes,
    clientPromise,
    client: initialClient,
  } = useContext(context);

  const serverTreatment = useServerTreatment(flagName);

  const getTreatment = useCallback(
    (client: SplitIO.IClient | undefined | null) => {
      if (override && override.treatment) {
        return override.treatment;
      }
      if (!client) return undefined;

      return client.getTreatment(flagName, {
        ...attributes,
        ...overrideAttributes,
      });
    },
    [attributes, flagName, overrideAttributes, override],
  );

  const [treatment, setTreatment] = useState<string | undefined>(() =>
    initialClient ? getTreatment(initialClient) : serverTreatment,
  );

  useEffect(() => {
    void (async () => {
      const client = await clientPromise;

      setTreatment(getTreatment(client));
    })();
  }, [clientPromise, getTreatment]);

  return treatment;
};

export {
  SplitContext,
  Provider,
  defaultValue,
  useSplit,
  useTreatment,
  useTreatmentWithConfig,
};
